// Externals
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';

// Services
import * as usersService from '../services/users';
import * as shopService from '../services/shop';

// Store
import type { AppThunk } from '../store';

// Types
import type { Shop } from '../types/shop.model';
import type { User } from '../types/user.model';
import jwtDecode from 'jwt-decode';

export interface AuthenticationState {
  isAuthenticated: boolean;
  isInitialized: boolean;
  isAdmin: boolean;
  isPayoutManager: boolean;
  shops: Shop[];
  user?: User;
}

const initialState: AuthenticationState = {
  isAuthenticated: false,
  isInitialized: false,
  isAdmin: false,
  isPayoutManager: false,
  shops: [],
  user: undefined
};

type DecodedToken = {
  isAdmin: boolean;
  isPayoutManager: boolean;
  email: string;
};

export const initialize = createAsyncThunk<
  { isAuthenticated: boolean; isAdmin: boolean; isPayoutManager: boolean; shops: Shop[]; user: User | undefined },
  void
>('authentication/initialize', async (_, thunkAPI) => {
  // Get Core API Token
  const payout_api_access_token: string = window.localStorage.getItem('payoutApiAccessToken') as string;
  const api_access_token: string = window.localStorage.getItem('apiAccessToken') as string;

  // Get user
  if (api_access_token && payout_api_access_token) {
    const decodedToken = jwtDecode<DecodedToken>(api_access_token);

    const shopsResult = await shopService.mine();
    if ((!shopsResult?.shops || shopsResult.shops.length === 0) && !decodedToken.isAdmin)
      throw new Error("Pour accéder à la plateforme, veuillez d'abord associer un commerce à votre compte.");

    if (!decodedToken.isAdmin) {
      const { exists } = await shopService.exists({ id: shopsResult.shops?.[0]?.id });
      if (!exists) throw new Error("Votre compte n'a pas encore été configuré par votre administrateur.");
    }

    return {
      isAuthenticated: true,
      isAdmin: decodedToken.isAdmin,
      isPayoutManager: decodedToken.isPayoutManager,
      shops: shopsResult.shops,
      user: { email: decodedToken.email }
    };
  } else {
    return {
      isAuthenticated: false,
      isAdmin: false,
      isPayoutManager: false,
      shops: [],
      user: undefined
    };
  }
});

export const signIn = createAsyncThunk<
  { isAuthenticated: boolean; isAdmin: boolean; isPayoutManager: boolean; shops: Shop[]; user: User },
  { email: string; password: string }
>('authentication/sign-in', async ({ email, password }, thunkAPI) => {
  // Fetch Core API Token
  const { token, legacyToken, payoutToken } = await usersService.signIn({
    email,
    password
  });

  window.localStorage.setItem('apiAccessToken', token);
  window.localStorage.setItem('coreApiAccessToken', legacyToken);
  window.localStorage.setItem('payoutApiAccessToken', payoutToken);

  const decodedToken = jwtDecode<DecodedToken>(token);

  const shopsResult = await shopService.mine();
  if ((!shopsResult?.shops || shopsResult.shops.length === 0) && !decodedToken.isAdmin)
    throw new Error("Pour accéder à la plateforme, veuillez d'abord créer un commerce.");

  if (!decodedToken.isAdmin) {
    const { exists } = await shopService.exists({ id: shopsResult.shops?.[0]?.id });
    if (!exists) throw new Error("Votre compte n'a pas encore été configuré par votre administrateur.");
  }

  return {
    isAuthenticated: true,
    isAdmin: decodedToken.isAdmin,
    isPayoutManager: decodedToken.isPayoutManager,
    shops: shopsResult.shops,
    user: {
      email: email
    }
  };
});

const slice = createSlice({
  name: 'authentication',
  initialState,
  reducers: {
    logout(state: AuthenticationState, action: PayloadAction<void>): void {
      state.isAuthenticated = false;
      state.isAdmin = false;
      state.isPayoutManager = false;
      state.user = undefined;
      state.shops = [];
    }
  },
  extraReducers: (builder) => {
    builder.addCase(initialize.fulfilled, (state, action): void => {
      state.isAuthenticated = action.payload.isAuthenticated;
      state.isInitialized = true;
      state.isAdmin = action.payload.isAdmin;
      state.isPayoutManager = action.payload.isPayoutManager;
      state.user = action.payload.user;
      state.shops = action.payload.shops;
    });
    builder.addCase(initialize.rejected, (state, action): void => {
      state.isAuthenticated = false;
      state.isInitialized = true;
      state.isAdmin = false;
      state.isPayoutManager = false;
      state.user = undefined;
      state.shops = [];
    });
    builder.addCase(signIn.fulfilled, (state, action): void => {
      state.isAuthenticated = action.payload.isAuthenticated;
      state.isAdmin = action.payload.isAdmin;
      state.isPayoutManager = action.payload.isPayoutManager;
      state.user = action.payload.user;
      state.shops = action.payload.shops;
    });
    builder.addCase(signIn.rejected, (state, action): void => {
      state.isAuthenticated = false;
      state.isAdmin = false;
      state.isPayoutManager = false;
      state.user = undefined;
      state.shops = [];
    });
  }
});

export const { reducer } = slice;

export const logout =
  (): AppThunk =>
  async (dispatch): Promise<void> => {
    window.localStorage.removeItem('coreApiAccessToken');
    window.localStorage.removeItem('payoutApiAccessToken');

    dispatch(slice.actions.logout());
  };

export default slice;
